import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Eye, Calendar, Edit, Trash2, PlayCircle, Star } from 'lucide-react';
import { formatDuration, getLessonViews, getLessonDuration } from '../../utils/videoUtils';
import LessonThumbnailSection from '../lesson/LessonThumbnailSection';

/**
 * TeacherLessonCard - عرض بطاقة الدرس في صفحة المدرس
 * يستخدم getVideoThumbnail للحصول على الصورة المصغرة الصحيحة
 */
function TeacherLessonCard({ 
  lesson, 
  duration, 
  views, 
  contentType, 
  badgeColor, 
  onPlay, 
  onDelete,
  lessonAnalytics 
}) {
  const navigate = useNavigate();
  
  return (
    <div className="group bg-white rounded-[2.5rem] border border-gray-100 shadow-sm hover:shadow-2xl transition-all duration-500 flex flex-col overflow-hidden">
      <LessonThumbnailSection lesson={lesson} onClickLesson={() => onPlay(lesson)} />

      {/* معلومات الدرس */}
      <div className="p-6 flex flex-col flex-1">
        <div className="bg-primary-50 text-primary-600 text-[10px] font-black px-3 py-1 rounded-lg w-fit uppercase mb-4 tracking-tighter">
          {lesson.course_title}
        </div>

        <h3 className="font-black text-gray-900 text-lg mb-2 line-clamp-1 group-hover:text-primary-600 transition-colors">{lesson.title}</h3>
        
        <p className="text-gray-500 text-sm mb-6 line-clamp-2 leading-relaxed flex-1">{lesson.description || 'لا يوجد وصف متاح لهذا الدرس حالياً.'}</p>

        {/* Stats Row - مدة + مشاهدات */}
        <div className="flex items-center justify-between text-[11px] font-black text-gray-400 py-4 border-t border-gray-50 mt-auto">
          <div className="flex items-center gap-3">
      {lesson.bunny_video_id && (
          <div className="flex items-center gap-1.5 bg-gray-50 px-3 py-1.5 rounded-xl border border-gray-100">
          <Eye size={14} className="text-blue-500" />
          <span className="text-gray-800">
          {lessonAnalytics?.[lesson.id] ?? views ?? getLessonViews(lesson) ?? 0} مشاهدة
          </span>
           </div>
       )}

       {/* Lesson average rating (if available) */}
       {(lesson.average_rating || lesson.rating || lesson.user_rating) && (
         <div className="flex items-center gap-1.5 bg-gray-50 px-3 py-1.5 rounded-xl border border-gray-100">
           <Star size={14} className="text-yellow-400" />
           <span className="text-gray-800">{Number(lesson.average_rating ?? lesson.rating ?? lesson.user_rating ?? 0).toFixed(1)}</span>
         </div>
       )}
       
         {(duration > 0 || getLessonDuration(lesson) > 0) && (
          <div className="flex items-center gap-1.5 bg-gray-50 px-3 py-1.5 rounded-xl border border-gray-100">
          <Clock size={14} className="text-emerald-500" />
          <span className="text-gray-800">{formatDuration(duration > 0 ? duration : getLessonDuration(lesson))}</span>
           </div>
       )}
          </div>
          <div className="flex items-center gap-1.5">
            <Calendar size={14} className="text-primary-500" />
            <span>{lesson.created_at ? new Date(lesson.created_at).toLocaleDateString('ar-EG') : 'غير محدد'}</span>
          </div>
        </div>

        {/* الأزرار */}
        <div className="grid grid-cols-4 gap-2 mt-4">
          
          {/* زر تشغيل */}
          <button
            onClick={() => onPlay(lesson)}
            className="col-span-2 bg-primary-600 text-white font-black py-3 rounded-xl flex items-center justify-center gap-2 hover:bg-primary-700 transition-all shadow-lg shadow-primary-100 active:scale-95 text-xs"
          >
            <PlayCircle size={16} /> تشغيل الآن
          </button>

          {/* زر تحرير */}
          <button 
            onClick={() => navigate(`/teacher/lessons/${lesson.id}/edit`, {
              state: {
                lesson: {
                  id: lesson.id,
                  title: lesson.title,
                  description: lesson.description,
                  video_url: lesson.video_url,
                  course_id: lesson.course_id,
                  course_title: lesson.course_title,
                  content_type: lesson.content_type || 'video',
                  is_free: lesson.is_free !== undefined ? lesson.is_free : false,
                  free_until_date: lesson.free_until_date,
                  thumbnail_url: lesson.thumbnail_url,
                  is_active: lesson.is_active !== undefined ? lesson.is_active : true
                }
              }
            })}
            className="bg-gray-50 text-gray-500 border border-gray-100 rounded-xl flex items-center justify-center hover:bg-white hover:border-blue-500 hover:text-blue-600 transition-all"
            title="تحرير الدرس"
          >
            <Edit size={18} />
          </button>

          {/* زر حذف */}
          <button
            onClick={() => onDelete(lesson)}
            className="bg-red-50 text-red-600 border border-red-100 rounded-xl flex items-center justify-center hover:bg-red-600 hover:text-white transition-all shadow-sm"
            title="حذف الدرس"
          >
            <Trash2 size={18} />
          </button>

        </div>
      </div>
    </div>
  );
};

export default TeacherLessonCard;
